import React from 'react';
import i18n from '../../../../../../../i18n/i18n';
import { useTranslation } from 'react-i18next';
import { CounterLabel, Accordion, FieldErrors, Icon } from '@weezevent/nacre';
import PriceInput from './PriceInput';
import css from '../../../../styles.module.css';

export default function RateInputs({ selected_rate, rates, form, bookingSelectionAuto, remaining_session_quota, unselectRate = () => {}, onChangeSelectedRate = () => {} }) {
    const { t } = useTranslation();
    const { language: locale } = i18n;
    const { currency } = form;

    const currencyOutput = Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency
    });

    const handleUnselect = React.useCallback(() => {
        unselectRate([selected_rate]);
    }, [unselectRate, selected_rate]);

    const handleQuantityChange = React.useCallback(
        (e, value) => {
            onChangeSelectedRate(selected_rate, 'quantity', value);
        },
        [onChangeSelectedRate, selected_rate]
    );

    const handleUnitPriceChange = React.useCallback(
        value => {
            onChangeSelectedRate(selected_rate, 'price', value);
        },
        [onChangeSelectedRate, selected_rate]
    );

    const quotaRate = React.useMemo(() => {
        /**
         * Get the quotas available on each level store in rate
         */
        return rates.find(rate => rate.id === selected_rate.rate.id).quotas_details;
    }, [rates, selected_rate]);

    const availableQuotas = React.useMemo(() => {
        /**
         * Get the min of available quotas -> Quota available on this rate
         */
        return Math.min(...Object.values(quotaRate).filter(value => value !== null && value !== Infinity));
    }, [quotaRate, selected_rate.quantity]);

    const remainingQuotas = React.useMemo(() => {
        return availableQuotas > 0 ? availableQuotas : 0;
    }, [availableQuotas, selected_rate, selected_rate.quantity, form, remaining_session_quota, quotaRate]);

    const hasQuotas = React.useMemo(() => {
        /**
         * Return a boolean to know if rate have quota (execept infinity or null quota)
         */
        return Object.values(quotaRate).some(el => el !== Infinity && el !== null);
    }, [quotaRate]);

    const quotasExceeded = React.useMemo(() => {
        /**
         * Return a boolean to know if quotas has been exceeded
         */
        return availableQuotas < 0;
    }, [selected_rate, availableQuotas]);

    const price = React.useMemo(() => {
        /**
         * Format price
         */
        return parseFloat(selected_rate.price).toFixed(2);
    }, [selected_rate.price]);

    const totalAmount = React.useMemo(() => {
        /**
         * Format total amount
         */
        return (selected_rate.quantity * selected_rate.price).toFixed(2);
    }, [selected_rate.quantity, selected_rate.price]);

    const hasFees = React.useMemo(() => {
        /**
         * Boolean to know if rate has fees
         */
        return selected_rate.fees > 0;
    }, [selected_rate]);

    const feesHigherThanMinPrice = React.useMemo(() => {
        /**
         * boolean to know if fees are higher than price
         */
        return selected_rate.price > 0 && selected_rate.price < selected_rate.fees;
    }, [selected_rate]);

    const contentStyle = React.useMemo(() => {
        if (!hasFees && !hasQuotas) {
            return { height: '90px' };
        }
        if (hasFees && !hasQuotas) {
            return { height: '100px' };
        }
        return { height: '125px', marginTop: '-15px' };
    }, [hasFees, hasQuotas]);

    return (
        <Accordion
            classname="accordion-override"
            disableToggle={true}
            openByDefault
            title={
                <div className={css['rate-title']}>
                    <div className={css['booking-rates-title']}>{selected_rate.rate.getName()}</div>
                    <div className={css['rate-subtitle']} onClick={handleUnselect}>
                        {t('ticket.common.action.delete')}
                    </div>
                </div>
            }
        >
            <div className={css['content']} style={contentStyle}>
                <QuantitySelector
                    hasQuotas={hasQuotas}
                    selected_rate={selected_rate}
                    form={form}
                    bookingSelectionAuto={bookingSelectionAuto}
                    handleQuantityChange={handleQuantityChange}
                    remainingQuotas={remainingQuotas}
                />

                <span className={css['symbol']}>x</span>

                <PriceSelector
                    hasFees={hasFees}
                    price={price}
                    handleUnitPriceChange={handleUnitPriceChange}
                    selected_rate={selected_rate}
                    currencyOutput={currencyOutput}
                    currency={currency}
                />

                <span className={css['symbol']}>=</span>

                <PriceInput title={t('ticket.discountForm.grid.tarif-ttc')} value={totalAmount} disabled={true} currency={currency} />
            </div>

            {quotasExceeded && (
                <div className={css['quotas-details-container']}>
                    <FieldErrors
                        className={css['rates-manager-quotas-warning']}
                        errors={[<Icon error name="message-error-inverted" label="test" color="target" />, t('attendee.order.quotaOver')]} // eslint-disable-line
                    />
                </div>
            )}
            {feesHigherThanMinPrice && (
                <div className={css['quotas-details-container']}>
                    <FieldErrors
                        className={css['rates-manager-quotas-warning']}
                        errors={[<Icon error name="message-error-inverted" label="test" color="target" />, t('attendee.order.priceTooLow')]} // eslint-disable-line
                    />
                </div>
            )}
        </Accordion>
    );
}

const PriceSelector = React.memo(({ hasFees, price, handleUnitPriceChange, selected_rate, currencyOutput, currency }) => {
    const { t } = useTranslation();
    if (!hasFees) {
        return <PriceInput title={t('attendee.order.unitPrice')} value={price} min={selected_rate.fees} onChange={handleUnitPriceChange} currency={currency} />;
    }

    return (
        <div className={`${css['price-input']} wz-field-block`}>
            <PriceInput title={t('attendee.order.unitPrice')} value={price} min={selected_rate.fees} onChange={handleUnitPriceChange} currency={currency} />

            <div className={`${css['rates-manager-quotas-left']} ${css['rates-manager-fees']}`}>{t('attendee.order.fees', { fee: currencyOutput.format(selected_rate.fees) })}</div>
        </div>
    );
});

const QuantitySelector = React.memo(({ hasQuotas, selected_rate, form, bookingSelectionAuto, handleQuantityChange, remainingQuotas }) => {
    const { t } = useTranslation();

    if (!hasQuotas) {
        return (
            <CounterLabel
                label={t('ticket.common.quantity')}
                numberDecimals={0}
                min={selected_rate.rate.cart_min_quantity ? selected_rate.rate.cart_min_quantity : 1}
                disabled={form.use_inventories && !bookingSelectionAuto}
                onChange={handleQuantityChange}
                step={1}
                value={selected_rate.quantity}
                hideArrows
            />
        );
    }
    return (
        <div className={`${css['quantity-input']} wz-field-block`}>
            <CounterLabel
                label={t('ticket.common.quantity')}
                numberDecimals={0}
                min={selected_rate.rate.cart_min_quantity ? selected_rate.rate.cart_min_quantity : 1}
                disabled={form.use_inventories && !bookingSelectionAuto}
                onChange={handleQuantityChange}
                step={1}
                value={selected_rate.quantity}
                hideArrows
            />
            {hasQuotas && <div className={css['rates-manager-quotas-left']}>{t('attendee.order.rate_quota', { quota: remainingQuotas })}</div>}
        </div>
    );
});
